import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { addKlachtAction, KlachtError } from '../../../lib/klachten';
import { useKlachtDialogStore } from '../../../stores/klacht-dialog-store';

interface Props {
  /** Callback na success — parent re-fetcht klacht-detail. */
  onAdded?: () => void;
}

/**
 * Action-dialog: "Geen actie / alleen excuses". Eén veld (toelichting),
 * geen logistiek of financieel gevolg. Wordt opgeslagen als Klacht_Update__c
 * met type='ActionAdded' + bron-tag.
 */
export function AddNoActionDialog({ onAdded }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'addNoAction';
  const klachtId = payload.klachtId;

  const [toelichting, setToelichting] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setToelichting('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!klachtId || !toelichting.trim()) {
      setError('Toelichting is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addKlachtAction({
        klachtId,
        actionType: 'NoAction',
        params: { toelichting: toelichting.trim() },
      });
      close();
      onAdded?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Toevoegen mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, toelichting, close, onAdded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Alleen excuses (geen actie)
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Leg vast waarom er geen vervolgactie nodig is.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">
              Toelichting <span className="text-red-600">*</span>
            </span>
            <textarea
              value={toelichting}
              onChange={(e) => setToelichting(e.target.value)}
              rows={4}
              placeholder="Bv. klant accepteert excuses, geen verdere actie afgesproken."
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !toelichting.trim()}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !toelichting.trim()
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-700 text-white hover:bg-slate-800'
            )}
          >
            {submitting ? 'Bezig…' : 'Vastleggen'}
          </button>
        </div>
      </div>
    </div>
  );
}
